import React from 'react'
import { useTranslation } from 'react-i18next'
import appleStoreImgFr from '../../../assets/icons/app-store-fr.png'
import appleStoreImgEn from '../../../assets/icons/apple-store.svg'
import googlePlayStoreImgFr from '../../../assets/icons/google-playstore-fr.svg'
import googlePlayStoreImgEn from '../../../assets/icons/google-playstore.svg'
import { toSafeExternalUrl } from '../../../utils/url/safe-external-url'
import { COLORS } from './tokens'

interface Props {
    appStoreUrl: string
    playStoreUrl: string
    /** Optional callback (used for breadcrumb logging) when a badge is clicked. */
    onBadgeClick?: () => void
}

const badgeImgStyle: React.CSSProperties = {
    width: '100%',
    height: 42,
    objectFit: 'contain',
}

const badgeAnchorStyle: React.CSSProperties = {
    flex: 1,
    maxWidth: 168,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

interface BadgeProps {
    href: string | null
    src: string
    alt: string
    onClick?: () => void
}

/**
 * Renders the store badge as an `<a>` when we have a known-safe URL, or as a
 * non-clickable `<span>` fallback when the URL is missing / invalid (e.g. an
 * env misconfig). Never emits a link with an unvalidated `href`.
 */
const StoreBadge: React.FC<BadgeProps> = ({ href, src, alt, onClick }) => {
    const img = <img src={src} alt={alt} style={badgeImgStyle} />
    if (!href) {
        return (
            <span style={badgeAnchorStyle} aria-disabled="true">
                {img}
            </span>
        )
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onClick}
            style={badgeAnchorStyle}
        >
            {img}
        </a>
    )
}

/**
 * Subordinate "get the app" footer — a hairline divider, a quiet prompt, and
 * the official store badges (App Store listed first per Apple's marketing
 * guidelines). Deliberately low-emphasis so it never competes with the primary
 * action above it.
 */
export const AppDownloadCard: React.FC<Props> = ({
    appStoreUrl,
    playStoreUrl,
    onBadgeClick,
}) => {
    const { t, i18n } = useTranslation()
    const language = i18n.resolvedLanguage
    // i18n.resolvedLanguage can be a region-qualified locale ("en-GB",
    // "en-US", "fr-CA"). A strict `=== 'en'` check would treat anything
    // other than bare "en" as non-English, so en-GB users would get French
    // assets. Detect French explicitly; default everything else to English.
    const isFrench = language?.toLowerCase().startsWith('fr') ?? false

    // Protocol allow-list rather than DOMPurify — sanitising a plain string
    // doesn't validate scheme, so a malformed env var with a javascript:
    // or data: URL would land in `href` unchanged. Invalid URLs render as
    // a non-link span (no href) and log to Sentry on first render.
    const safeAppStoreUrl = toSafeExternalUrl(appStoreUrl)
    const safePlayStoreUrl = toSafeExternalUrl(playStoreUrl)

    const appleStoreImg = isFrench ? appleStoreImgFr : appleStoreImgEn
    const googlePlayImg = isFrench ? googlePlayStoreImgFr : googlePlayStoreImgEn

    return (
        <div
            style={{
                marginTop: 22,
                paddingTop: 18,
                borderTop: `1px solid ${COLORS.neutral200}`,
            }}
        >
            <p
                style={{
                    fontSize: 12.5,
                    color: COLORS.neutral500,
                    marginTop: 0,
                    marginBottom: 12,
                    textAlign: 'center',
                    lineHeight: 1.4,
                }}
            >
                {t('MagicLinkChooser.downloadAppPrompt')}{' '}
                <span style={{ color: COLORS.action }}>
                    {t('MagicLinkChooser.downloadAppCta')}
                </span>
            </p>
            <div
                style={{
                    display: 'flex',
                    gap: 10,
                    justifyContent: 'center',
                }}
            >
                <StoreBadge
                    href={safeAppStoreUrl}
                    src={appleStoreImg}
                    alt={t('MagicLinkChooser.appStoreBadgeAlt')}
                    onClick={onBadgeClick}
                />
                <StoreBadge
                    href={safePlayStoreUrl}
                    src={googlePlayImg}
                    alt={t('MagicLinkChooser.playStoreBadgeAlt')}
                    onClick={onBadgeClick}
                />
            </div>
        </div>
    )
}
